import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import PeopleImportModal from './PeopleImportModal';
import PrintReportDocument from '../PrintReportDocument';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { resolveTabs } from '../../i18n/resolveTabs';
import HrYearEndSimulationPage from './HrYearEndSimulationPage';
import HrEvolutionPage from './HrEvolutionPage';

/* Chiffres clés — les nombres, pas leur lecture.

   La section « Évolution », créée le matin même, y est absorbée le soir : les
   trois onglets répondent à la même question à trois horizons, et les séparer
   en deux entrées de menu obligeait à faire l'aller-retour pour comparer.
   Le récit (section « Synthèse ») en raconte la conclusion ; ici on vient
   chercher le détail chiffré, sans commentaire.

   Ordre des onglets = ÉLARGISSEMENT DE L'HORIZON : la masse de l'exercice,
   son détail par personne (Effectif, arrivé de « Charges de personnel » le
   2026-08-21 au soir — c'est une lecture de chiffres, sa place est ici), puis
   la rétrospective d'un an. Sans flèches : ce sont trois regards, pas un ordre
   de construction.

   L'évolution PLURIANNUELLE est partie en Planification le 2026-08-26 : elle
   projette N+1 → N+5 avec les taux de N, quand cette section ne lit que du
   réel constaté.

   Gabarit unique : <PageShell framed> + <Tabs> + sous-pages en embedded. */
const TAB_DEFS = [
  /* (Le RÉCIT est passé dans la section « Accueil » le 2026-09-01 : on raconte
     l'année en arrivant dans le module, pas au troisième clic. Il avait ouvert
     cette section le 2026-08-27, venu de son ancienne section « Synthèse ».) */
  { id: 'mass',          labelKey: 'people:synthese.tabs.mass' },
  { id: 'annuelle',      labelKey: 'people:synthese.tabs.annuelle' },
  /* (Le DÉTAIL PAR PERSONNE est passé dans « Charges » le 2026-09-18 : cette
     section regarde l'année d'ensemble, le nominatif appartient à l'écran où
     l'on chiffre. Sa page reste routée.) */
];

export default function PeopleSynthesisPage({ activeTenant, setPage, year, onYearChange, addToast, onGoPeople }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('mass');
  // Même préférence d'unité que les autres sections People (par tenant).
  const [unit, setUnit] = usePersistentValue(`people_unit_${activeTenant?.id || ''}`, 'chf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const TABS = resolveTabs(TAB_DEFS, t);

  // `u` explicite : l'écran suit la bascule d'unité, l'export force CHF.
  const renderTabFor = (id, u) => {
    switch (id) {
      case 'mass':
        return <HrYearEndSimulationPage embedded unit={u} activeTenant={activeTenant} setPage={setPage} year={year} onYearChange={onYearChange} />;
      case 'annuelle':
        return <HrEvolutionPage embedded unit={u} activeTenant={activeTenant} year={year} onYearChange={onYearChange} />;
      default:
        return null;
    }
  };

  /* Export Excel — la section n'en avait aucun : les chiffres clés se lisaient
     à l'écran et se recopiaient à la main. Un onglet = une feuille, en CHF. */
  /* Onglets exportables : « Masse » et « Évolution annuelle » ne rendent que des
     tuiles KPI et des graphes, en <div> — le moteur ne lit que les <table>, ils
     ne produiraient aucune feuille et feraient croire à un export tronqué. */
  /* Le récit non plus n'a pas de tableau : ce sont des phrases et des figures
     clés, il ne produirait aucune feuille. Reste « Détail des charges », seul
     onglet tabulaire de la section — jusqu'à son arrivée, l'export n'avait
     aucune feuille à écrire. */
  const EXPORT_TABS = TABS.filter((tb) => tb.id !== 'mass' && tb.id !== 'annuelle' && tb.id !== 'story');
  const [showData, setShowData] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const excel = useHubExcelExport({
    tabs: EXPORT_TABS.map(tb => ({ label: tb.label, render: () => renderTabFor(tb.id, 'chf') })),
    fileBase: t('people:synthese.title'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  /* PDF de synthèse — LES TROIS onglets, dans l'ordre demandé le 2026-08-26 :
     le coût total, l'évolution N-1 → N, puis l'effectif mensuel. L'effectif n'y
     figurait pas du tout, et les deux autres sortaient dans l'ordre du registre
     d'impression, donc à l'envers. La trajectoire pluriannuelle est partie avec
     son onglet ; elle s'imprime depuis le PDF de Planification, qui la portait
     déjà. */
  useRegisterPageExports(useMemo(() => ([
    { id: 'excel', label: 'Excel', run: excel.run },
    { id: 'pdf', label: 'PDF', run: () => setShowPrint(true) },
  ]), [excel.run]));

  return (
    <PageShell framed kind="report" unit={pageUnit} title={t('people:synthese.title')}>
      <Tabs
        tabs={TABS}
        active={tab}
        onChange={setTab}
      />
      <div className="page-shell__panel">{renderTabFor(tab, unit)}</div>
      {showData && activeTenant && (
        <PeopleImportModal
          tenantId={activeTenant.id}
          year={year}
          addToast={addToast}
          onAddPerson={() => onGoPeople && onGoPeople({ tab: 'people', action: 'add' })}
          onClose={() => setShowData(false)}
        />
      )}
      {showPrint && (
        <PrintReportDocument
          activeTenant={activeTenant}
          year={year}
          reports={['people-story', 'people-cockpit', 'staff-evolution']}
          mode="pdf"
          onClose={() => setShowPrint(false)}
        />
      )}

      {excel.stage}
    </PageShell>
  );
}
